import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format, parseISO, isBefore, isAfter } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import {
  Plus,
  Edit,
  Trash2,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Users,
  GraduationCap,
  Mountain,
  Trophy,
  UserCheck,
  Bell,
  Eye,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { API } from "@/App";
import { parseTurkishNumber } from "@/utils/numberUtils";
import { CurrencyInput } from "@/components/ui/currency-input";
import { useTranslation } from "react-i18next";

// Event type icons
const EVENT_ICONS = {
  general: CalendarIcon,
  workshop: GraduationCap,
  outdoor: Mountain,
  competition: Trophy,
  social: Users,
};

const EVENT_COLORS = {
  blue: "bg-blue-100 text-blue-700 border-blue-200",
  purple: "bg-purple-100 text-purple-700 border-purple-200",
  green: "bg-green-100 text-green-700 border-green-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  pink: "bg-pink-100 text-pink-700 border-pink-200",
};

export default function EventsModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "events", "surveys"]);

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [trainersList, setTrainersList] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTab, setActiveTab] = useState("upcoming");
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [eventToCancel, setEventToCancel] = useState(null);
  
  // Form
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    event_type: "general",
    date: "",
    start_time: "10:00",
    end_time: "11:00",
    location: "",
    max_participants: "",
    is_free: true,
    price: "",
    trainer_id: "",
    send_notification: true,
  });
  
  // Participants
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Date picker popover state for mobile
  const [eventDatePickerOpen, setEventDatePickerOpen] = useState(false);

  useEffect(() => {
    if (businessId) {
      fetchEvents();
      fetchEventTypes();
      fetchTrainers();
    }
  }, [businessId]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/events/business/${businessId}?include_past=true`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await fetch(`${API}/events/types`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setEventTypes(data);
      }
    } catch (error) {
      console.error("Error fetching event types:", error);
    }
  };

  const fetchTrainers = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API}/businesses/${businessId}/trainers`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        setTrainersList(data);
      }
    } catch (error) {
      console.error("Error fetching trainers:", error);
    }
  };

  const fetchParticipants = async (eventId) => {
    setLoadingParticipants(true);
    try {
      const response = await fetch(
        `${API}/events/business/${businessId}/${eventId}/participants`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setParticipants(data);
      }
    } catch (error) {
      console.error("Error fetching participants:", error);
    } finally {
      setLoadingParticipants(false);
    }
  };

  // Validate form before submit
  const validateForm = () => {
    if (!form.title.trim()) {
      toast.error(t("events:toast_etkinlikBasligiGerekli"));
      return false;
    }
    if (!form.date) {
      toast.error(t("common:toast_tarihSecmelisiniz"));
      return false;
    }
    if (!form.start_time) {
      toast.error(t("common:toast_baslangicSaatiSecmelisiniz"));
      return false;
    }
    if (!form.end_time) {
      toast.error(t("common:toast_bitisSaatiSecmelisiniz"));
      return false;
    }
    
    // Check if end time is after start time
    const [startHour, startMin] = form.start_time.split(":").map(Number);
    const [endHour, endMin] = form.end_time.split(":").map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    if (endMinutes <= startMinutes) {
      toast.error(t("common:toast_bitisSaatiBaslangicSaatindenSonra"));
      return false;
    }
    
    return true;
  };

  const handleSaveEvent = async () => {
    if (!validateForm()) return;

    if (isSaving) return;
    setIsSaving(true);

    try {
      const url = editingEvent
        ? `${API}/events/business/${businessId}/${editingEvent.event_id}`
        : `${API}/events/business/${businessId}`;
      
      const response = await fetch(url, {
        method: editingEvent ? "PUT" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          max_participants: form.max_participants ? parseInt(form.max_participants) : null,
          price: form.is_free ? 0 : parseTurkishNumber(form.price) || 0,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (editingEvent && result.notified_count > 0) {
          toast.success(`Etkinlik güncellendi ve ${result.notified_count} katılımcıya bildirim gönderildi`);
        } else {
          toast.success(editingEvent ? t("events:eventUpdated") : t("events:eventCreated"));
        }
        fetchEvents();
        handleCloseCreateModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEvent = async () => {
    if (!eventToCancel || !cancelReason.trim()) {
      toast.error(t("common:cancelReasonRequired"));
      return;
    }

    try {
      const response = await fetch(
        `${API}/events/business/${businessId}/${eventToCancel.event_id}/cancel`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            reason: cancelReason,
            send_notification: true 
          }),
        }
      );

      if (response.ok) {
        toast.success(t("events:toast_etkinlikIptalEdildiVeUyelere"));
        fetchEvents();
        setShowCancelDialog(false);
        setEventToCancel(null);
        setCancelReason("");
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleMarkComplete = async (event) => {
    try {
      const response = await fetch(
        `${API}/events/business/${businessId}/${event.event_id}/complete`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        toast.success(t("events:toast_etkinlikTamamlandiOlarakIsaretlendi"));
        fetchEvents();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const response = await fetch(
        `${API}/events/business/${businessId}/${eventId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        toast.success(t("events:eventDeleted"));
        fetchEvents();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleOpenCreateModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setForm({
        title: event.title,
        description: event.description || "",
        event_type: event.event_type || "general",
        date: event.date,
        start_time: event.start_time || "10:00",
        end_time: event.end_time || "11:00",
        location: event.location || "",
        max_participants: event.max_participants?.toString() || "",
        is_free: event.is_free ?? true,
        price: event.price?.toString() || "",
        trainer_id: event.trainer_id || "",
        send_notification: true,
      });
    } else {
      setEditingEvent(null);
      const today = todayLocalString();
      setForm({
        title: "",
        description: "",
        event_type: "general",
        date: today,
        start_time: "10:00",
        end_time: "11:00",
        location: "",
        max_participants: "",
        is_free: true,
        price: "",
        trainer_id: "",
        send_notification: true,
      });
    }
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setEditingEvent(null);
  };

  const handleOpenParticipantsModal = async (event) => {
    setSelectedEvent(event);
    setShowParticipantsModal(true);
    await fetchParticipants(event.event_id);
  };

  const getIconComponent = (type) => {
    return EVENT_ICONS[type] || CalendarIcon;
  };

  // Filter events by status
  const now = new Date();
  const upcomingEvents = events.filter((e) => {
    const eventDate = new Date(e.date);
    return e.status !== "cancelled" && e.status !== "completed" && eventDate >= new Date(now.toDateString());
  });
  
  const pastEvents = events.filter((e) => {
    const eventDate = new Date(e.date);
    return e.status === "completed" || (e.status !== "cancelled" && eventDate < new Date(now.toDateString()));
  });
  
  const cancelledEvents = events.filter((e) => e.status === "cancelled");

  // Generate time options filtering end_time based on start_time
  const getEndTimeOptions = () => {
    const options = [];
    const [startHour, startMin] = form.start_time.split(":").map(Number);
    const startMinutes = startHour * 60 + startMin;
    
    for (let i = 0; i < 48; i++) {
      const hour = Math.floor(i / 2);
      const minute = (i % 2) * 30;
      const totalMinutes = hour * 60 + minute;
      
      if (totalMinutes > startMinutes) {
        const time = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        options.push(time);
      }
    }
    return options;
  };

  // Format date for display (Turkish format)
  const formatDisplayDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMMM yyyy, EEEE", { locale: getDateLocale() });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">{t('events:events')}</h1>
          <p className="text-grey text-sm">
            {t('events:organizeEventsForMembers')}
          </p>
        </div>
        <Button
          onClick={() => handleOpenCreateModal()}
          className="bg-navy hover:bg-navy/90 rounded-xl"
          data-testid="create-event-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('events:newEvent')}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start rounded-xl bg-white p-1 shadow-sm">
          <TabsTrigger value="upcoming" className="rounded-lg">
            {t('common:upcoming')} ({upcomingEvents.length})
          </TabsTrigger>
          <TabsTrigger value="past" className="rounded-lg">
            {t('common:completed')} ({pastEvents.length})
          </TabsTrigger>
          <TabsTrigger value="cancelled" className="rounded-lg">
            {t('common:cancelled')} ({cancelledEvents.length})
          </TabsTrigger>
        </TabsList>

        {/* Upcoming Events */}
        <TabsContent value="upcoming" className="mt-4">
          {upcomingEvents.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <CalendarIcon className="w-12 h-12 mx-auto text-grey/50 mb-4" />
                <p className="text-grey mb-4">{t("events:yaklasanEtkinlikYok")}</p>
                <Button
                  onClick={() => handleOpenCreateModal()}
                  className="bg-navy hover:bg-navy/90 rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('events:createFirstEvent')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {upcomingEvents.map((event) => {
                const IconComp = getIconComponent(event.event_type);
                return (
                  <Card key={event.event_id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`p-3 rounded-xl ${EVENT_COLORS[event.color] || EVENT_COLORS.blue}`}>
                            <IconComp className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-navy truncate">{event.title}</h3>
                            <div className="flex items-center gap-3 mt-1 text-sm text-grey flex-wrap">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-3.5 h-3.5" />
                                {formatDisplayDate(event.date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {event.start_time} - {event.end_time}
                              </span>
                              {event.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {event.location}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-blue-50">
                              {event.participant_count || 0}
                              {event.max_participants && `/${event.max_participants}`} Katılımcı
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenParticipantsModal(event)}
                            className="rounded-xl"
                          >
                            <Users className="w-4 h-4 mr-1" />
                            Katılımcılar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkComplete(event)}
                            className="rounded-xl text-green-600 border-green-200 hover:bg-green-50"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Tamamlandı
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenCreateModal(event)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEventToCancel(event);
                              setShowCancelDialog(true);
                            }}
                            className="h-8 w-8 p-0 text-red-500"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Past/Completed Events */}
        <TabsContent value="past" className="mt-4">
          {pastEvents.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-grey/50 mb-4" />
                <p className="text-grey">{t("events:henuzTamamlananEtkinlikYok")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pastEvents.map((event) => {
                const IconComp = getIconComponent(event.event_type);
                return (
                  <Card key={event.event_id} className="border-0 shadow-sm opacity-75">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="p-3 rounded-xl bg-green-100 text-green-700">
                            <CheckCircle2 className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-navy truncate">{event.title}</h3>
                              <Badge className="bg-green-100 text-green-700">{t('common:completed')}</Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm text-grey">
                              <span>{formatDisplayDate(event.date)}</span>
                              <span>{event.start_time} - {event.end_time}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">
                            {event.participant_count || 0} Katılımcı
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenParticipantsModal(event)}
                            className="rounded-xl"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Cancelled Events */}
        <TabsContent value="cancelled" className="mt-4">
          {cancelledEvents.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <XCircle className="w-12 h-12 mx-auto text-grey/50 mb-4" />
                <p className="text-grey">{t("events:i̇ptalEdilenEtkinlikYok")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {cancelledEvents.map((event) => (
                <Card key={event.event_id} className="border-0 shadow-sm opacity-60">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="p-3 rounded-xl bg-red-100 text-red-700">
                          <XCircle className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-navy truncate line-through">{event.title}</h3>
                            <Badge className="bg-red-100 text-red-700">{t('common:cancelled')}</Badge>
                          </div>
                          <p className="text-sm text-grey mt-1">
                            {formatDisplayDate(event.date)} - {event.start_time}
                          </p>
                          {event.cancel_reason && (
                            <p className="text-sm text-red-600 mt-1">
                              İptal sebebi: {event.cancel_reason}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEvent(event.event_id)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? t('common:editEvent') : t('common:createNewEvent')}
            </DialogTitle>
            <DialogDescription>
              Etkinlik detaylarını girin
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Etkinlik Başlığı *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={t("common:ph_ornYogaWorkshop")}
                  className="rounded-xl mt-1"
                />
              </div>
              
              <div>
                <Label>Etkinlik Tipi</Label>
                <Select
                  value={form.event_type}
                  onValueChange={(v) => setForm({ ...form, event_type: v })}
                >
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue placeholder={t("common:ph_tipSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Genel</SelectItem>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="outdoor">{t("common:acikHava")}</SelectItem>
                    <SelectItem value="competition">{t("common:yarisma")}</SelectItem>
                    <SelectItem value="social">Sosyal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Antrenör (Opsiyonel)</Label>
                <Select
                  value={form.trainer_id || "none"}
                  onValueChange={(v) => setForm({ ...form, trainer_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue placeholder={t("common:selectTrainer")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('trainers:noTrainer')}</SelectItem>
                    {trainersList.map((trainer) => (
                      <SelectItem key={trainer.user_id || trainer.trainer_id} value={trainer.user_id || trainer.trainer_id}>
                        {trainer.name} {trainer.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{t("common:dateRequired")}</Label>
                <Popover open={eventDatePickerOpen} onOpenChange={setEventDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal rounded-xl mt-1"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.date ? (
                        format(new Date(form.date), "d MMMM yyyy", { locale: getDateLocale() })
                      ) : (
                        <span className="text-muted-foreground">{t('common:selectDate')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                    <Calendar
                      mode="single"
                      selected={form.date ? new Date(form.date) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const formattedDate = format(date, "yyyy-MM-dd");
                          setForm({ ...form, date: formattedDate });
                          setEventDatePickerOpen(false);
                        }
                      }}
                      locale={tr}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus={false}
                    />
                  </PopoverContent>
                </Popover>
                {form.date && (
                  <p className="text-xs text-grey mt-1">
                    {formatDisplayDate(form.date)}
                  </p>
                )}
              </div>
              <div>
                <Label>Başlangıç *</Label>
                <Select
                  value={form.start_time}
                  onValueChange={(v) => {
                    setForm({ ...form, start_time: v });
                    // Reset end_time if it's before new start_time
                    const [newStartH, newStartM] = v.split(":").map(Number);
                    const [endH, endM] = form.end_time.split(":").map(Number);
                    if (endH * 60 + endM <= newStartH * 60 + newStartM) {
                      const newEndH = newStartH + 1;
                      setForm((prev) => ({
                        ...prev,
                        start_time: v,
                        end_time: `${newEndH.toString().padStart(2, "0")}:${newStartM.toString().padStart(2, "0")}`,
                      }));
                    }
                  }}
                >
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue placeholder={t("common:ph_saatSecin")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {Array.from({ length: 48 }, (_, i) => {
                      const hour = Math.floor(i / 2);
                      const minute = (i % 2) * 30;
                      const time = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
                      return (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bitiş *</Label>
                <Select
                  value={form.end_time}
                  onValueChange={(v) => setForm({ ...form, end_time: v })}
                >
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue placeholder={t("common:ph_saatSecin")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {getEndTimeOptions().map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>{t('business:location')}</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder={t("common:ph_ornAnaSalon")}
                className="rounded-xl mt-1"
              />
            </div>

            <div>
              <Label>{t('common:description')}</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t("events:ph_etkinlikHakkindaDetayliBilgi")}
                className="rounded-xl mt-1"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("events:maksimumKatilimci")}</Label>
                <Input
                  type="number"
                  value={form.max_participants}
                  onChange={(e) => setForm({ ...form, max_participants: e.target.value })}
                  placeholder={t("common:ph_sinirsizIcinBosBirakin")}
                  className="rounded-xl mt-1"
                  min="1"
                />
              </div>
              <div>
                <Label>{t("common:ucret")}</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Switch
                    checked={form.is_free}
                    onCheckedChange={(v) => setForm({ ...form, is_free: v })}
                  />
                  <span className="text-sm text-grey">
                    {form.is_free ? t('dashboard:subscription.free') : t('common:paid')}
                  </span>
                  {!form.is_free && (
                    <CurrencyInput
                      value={form.price}
                      onChange={(val) => setForm({ ...form, price: val })}
                      placeholder="0"
                      className="rounded-xl w-32"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
              <Bell className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <Label className="text-blue-800">{t("common:uyelereBildir")}</Label>
                <p className="text-xs text-blue-600">
                  {editingEvent
                    ? t("events:changesNotifiedToMembers")
                    : t("events:newEventAnnounced")}
                </p>
              </div>
              <Switch
                checked={form.send_notification}
                onCheckedChange={(v) => setForm({ ...form, send_notification: v })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={handleCloseCreateModal}
              className="rounded-xl"
              disabled={isSaving}
            >
              İptal
            </Button>
            <Button
              onClick={handleSaveEvent}
              className="bg-navy hover:bg-navy/90 rounded-xl"
              disabled={isSaving}
            >
              {isSaving ? "Kaydediliyor..." : (editingEvent ? t('settings:update') : t('settings:create'))}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Participants Modal */}
      <Dialog open={showParticipantsModal} onOpenChange={setShowParticipantsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {selectedEvent?.title} - Katılımcılar
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {loadingParticipants ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-navy" />
              </div>
            ) : participants.length === 0 ? (
              <p className="text-center text-grey py-8">{t("events:henuzKatilimciYok")}</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {participants.map((p, index) => (
                  <div
                    key={p.member_id || index}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl"
                  >
                    <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-navy" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-navy">{p.name || "Üye"}</p>
                      <p className="text-xs text-grey">
                        Kayıt: {p.registered_at && format(new Date(p.registered_at), "d MMM yyyy HH:mm", { locale: getDateLocale() })}
                      </p>
                    </div>
                    {p.attended && (
                      <Badge className="bg-green-100 text-green-700">Katıldı</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setShowParticipantsModal(false)}
              className="rounded-xl"
            >
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Event Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {t('common:cancelEvent')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  <span className="font-semibold text-navy">
                    &quot;{eventToCancel?.title}&quot;
                  </span>{" "}
                  etkinliğini iptal etmek istediğinize emin misiniz?
                </p>
                <div>
                  <Label>İptal Sebebi *</Label>
                  <Textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder={t("surveys:cancelReasonForMembers")}
                    className="rounded-xl mt-1"
                    rows={3}
                  />
                </div>
                <p className="text-sm text-amber-600">
                  Tüm katılımcılara iptal bildirimi gönderilecektir.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="rounded-xl"
              onClick={() => {
                setCancelReason("");
                setEventToCancel(null);
              }}
            >
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelEvent}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
              disabled={!cancelReason.trim()}
            >
              İptal Et
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
